import { useState, useEffect, useCallback, useRef } from 'react';
import { managementApi, developerApi, versionApi } from '../services/api';

/**
 * Centralized data hook for the Unified Dashboard.
 * Fetches executive + developer data based on current filters.
 * Eliminates duplicate fetching between old Management/Developer dashboards.
 */
export function useDashboardData({ platform, version, viewMode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Executive data
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [trendDays, setTrendDays] = useState(30);
  const [readiness, setReadiness] = useState([]);
  const [platformSummary, setPlatformSummary] = useState([]);
  const [rootCauses, setRootCauses] = useState([]);
  const [alerts, setAlerts] = useState({ active: [], history: [] });
  const [platformRisk, setPlatformRisk] = useState(null);

  // Developer data
  const [crashes, setCrashes] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [anr, setAnr] = useState({ byRam: [], byOs: [], byType: [] });
  const [aiRecs, setAiRecs] = useState([]);
  const [devSummary, setDevSummary] = useState(null);
  const [buildBreakdown, setBuildBreakdown] = useState([]);

  // Version data
  const [versions, setVersions] = useState([]);
  const [liveStatus, setLiveStatus] = useState(null);

  const abortRef = useRef(null);

  // Load executive data — filtered by selected version AND platform
  const loadExecutiveData = useCallback(async () => {
    try {
      const filterCtx = { version, platform: platform !== 'all' ? platform : undefined };
      const [summaryRes, readinessRes, platformRes, rootCauseRes, alertsRes] =
        await Promise.allSettled([
          managementApi.getSummary(version),
          managementApi.getLaunchReadiness(),
          managementApi.getPlatformSummary(),
          managementApi.getTopRootCauses(3, filterCtx),
          managementApi.getAlerts(7, filterCtx),
        ]);

      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data);
      if (readinessRes.status === 'fulfilled') setReadiness(readinessRes.value.data.versions || []);
      if (platformRes.status === 'fulfilled') setPlatformSummary(platformRes.value.data.platforms || []);
      if (rootCauseRes.status === 'fulfilled') setRootCauses(rootCauseRes.value.data.rootCauses || []);
      if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.data);
    } catch (err) {
      console.error('Executive data load failed:', err);
    }
  }, [version, platform]);

  // Load trend data separately (can change days)
  const loadTrend = useCallback(async (days) => {
    try {
      const p = platform !== 'all' ? platform : null;
      const res = p
        ? await managementApi.getPlatformTrend(days, p)
        : await managementApi.getTrend(days);
      setTrend(res.data.trend || []);
    } catch (err) {
      console.error('Trend load failed:', err);
    }
  }, [platform]);

  // Load platform-specific risk
  const loadPlatformRisk = useCallback(async () => {
    if (!platform || platform === 'all') {
      setPlatformRisk(null);
      return;
    }
    try {
      const res = await managementApi.getReleaseRiskPlatform(version, platform);
      setPlatformRisk(res.data);
    } catch {
      setPlatformRisk(null);
    }
  }, [platform, version]);

  // Load developer data
  const loadDeveloperData = useCallback(async () => {
    try {
      const params = {};
      if (version) params.appVersion = version;
      if (platform && platform !== 'all') params.platform = platform;

      const filterCtx = { version, platform: platform !== 'all' ? platform : undefined };
      const [summaryRes, crashRes, heatmapRes, anrRes, recsRes, buildRes] =
        await Promise.allSettled([
          developerApi.getSummary(version),
          developerApi.getCrashes(params),
          developerApi.getCrashHeatmap(),
          developerApi.getAnr(params).catch(() => ({ data: { byRam: [], byOs: [], byType: [] } })),
          developerApi.getAiRecommendations(filterCtx),
          developerApi.getBuildBreakdown({ days: 30, ...(platform && platform !== 'all' ? { platform } : {}) }),
        ]);

      if (summaryRes.status === 'fulfilled') setDevSummary(summaryRes.value.data);
      if (crashRes.status === 'fulfilled') setCrashes(crashRes.value.data.crashes || []);
      if (heatmapRes.status === 'fulfilled') setHeatmap(heatmapRes.value.data.heatmap || []);
      if (anrRes.status === 'fulfilled') setAnr(anrRes.value.data || { byRam: [], byOs: [], byType: [] });
      if (recsRes.status === 'fulfilled') setAiRecs(recsRes.value.data.recommendations || []);
      if (buildRes.status === 'fulfilled') setBuildBreakdown(buildRes.value.data.builds || []);
    } catch (err) {
      console.error('Developer data load failed:', err);
    }
  }, [platform, version]);

  // Load version list
  const loadVersions = useCallback(async () => {
    try {
      const [listRes, statusRes] = await Promise.allSettled([
        versionApi.list(platform),
        versionApi.liveStatus(platform !== 'all' ? platform : undefined),
      ]);
      if (listRes.status === 'fulfilled') setVersions(listRes.value.data.versions || []);
      if (statusRes.status === 'fulfilled') setLiveStatus(statusRes.value.data);
    } catch (err) {
      console.error('Version data load failed:', err);
    }
  }, [platform]);

  // Main load effect
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Always load versions
        await loadVersions();

        // Load view-specific data
        if (viewMode === 'executive') {
          await Promise.all([loadExecutiveData(), loadTrend(trendDays), loadPlatformRisk()]);
        } else {
          await loadDeveloperData();
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [viewMode, platform, version, trendDays, loadExecutiveData, loadTrend, loadPlatformRisk, loadDeveloperData, loadVersions]);

  // Change trend period
  const changeTrendDays = useCallback((days) => {
    setTrendDays(days);
    loadTrend(days);
  }, [loadTrend]);

  // Refresh all data
  const refresh = useCallback(async () => {
    setLoading(true);
    await loadVersions();
    if (viewMode === 'executive') {
      await Promise.all([loadExecutiveData(), loadTrend(trendDays), loadPlatformRisk()]);
    } else {
      await loadDeveloperData();
    }
    setLoading(false);
  }, [viewMode, trendDays, loadExecutiveData, loadTrend, loadPlatformRisk, loadDeveloperData, loadVersions]);

  return {
    loading, error,
    // Executive
    summary, trend, trendDays, readiness, platformSummary, rootCauses, alerts, platformRisk,
    // Developer
    crashes, heatmap, anr, aiRecs, devSummary, buildBreakdown,
    // Versions
    versions, liveStatus,
    // Actions
    changeTrendDays, refresh,
  };
}
